import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import { isSuggestionPopupOpen, closeSuggestionPopup } from './suggestion-popup-state'
import { Event } from '@nostr/tools/wasm'
import { Dispatch, useEffect, useMemo, useState } from 'react'
import { useAtomValue } from 'jotai'
import PostContent from './PostContent'
import Title from './Title'

export default function PostEditor({
  defaultContent = '',
  editingEvent,
  parentEvents,
  parentExternal,
  open,
  setOpen,
  openFromRelays
}: {
  defaultContent?: string
  editingEvent?: Event
  parentEvents?: Event[]
  parentExternal?: string
  open: boolean
  setOpen: Dispatch<boolean>
  openFromRelays?: string[]
}) {
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const [undoContent, setUndoContent] = useState(defaultContent)
  const [undoKey, setUndoKey] = useState(0)

  useEffect(() => {
    setUndoContent(defaultContent)
    setUndoKey((k) => k + 1)
  }, [defaultContent, editingEvent?.id])

  const content = useMemo(() => {
    return (
      <PostContent
        defaultContent={undoContent}
        editingEvent={editingEvent}
        parentEvents={parentEvents}
        parentExternal={parentExternal}
        close={() => setOpen(false)}
        open={handleOpen}
        openFromRelays={openFromRelays}
      />
    )
  }, [editingEvent, openFromRelays, setOpen, undoKey])

  if (isSmallScreen) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          className="h-full w-full p-0 border-none"
          side="bottom"
          hideClose
          onEscapeKeyDown={(e) => {
            if (isSuggestionPopupOpen()) {
              e.preventDefault()
              closeSuggestionPopup()
            }
          }}
        >
          <ScrollArea className="px-4 h-full max-h-screen">
            <div className="space-y-4 px-2 py-6">
              <SheetHeader>
                <SheetTitle className="text-start">
                  <Title parentEvents={parentEvents} parentExternal={parentExternal} />
                </SheetTitle>
                <SheetDescription className="hidden" />
              </SheetHeader>
              {content}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="p-0 max-w-2xl"
        withoutClose
        onEscapeKeyDown={(e) => {
          if (isSuggestionPopupOpen()) {
            e.preventDefault()
            closeSuggestionPopup()
          }
        }}
      >
        <ScrollArea className="px-4 h-full max-h-screen">
          <div className="space-y-4 px-2 py-6">
            <DialogHeader>
              <DialogTitle>
                <Title parentEvents={parentEvents} parentExternal={parentExternal} />
              </DialogTitle>
              <DialogDescription className="hidden" />
            </DialogHeader>
            {content}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )

  function handleOpen(content?: string) {
    if (content !== undefined) {
      setUndoContent(content)
      setUndoKey((k) => k + 1)
    }
    setOpen(true)
  }
}
